'use client';

import type { ReactNode } from 'react';
import { useTranslation } from '../lib/i18n';
import { useEscapeClose } from '../lib/useEscapeClose';
import { useDraggable } from '../lib/useDraggable';
import { useExitAnimation } from '../lib/useExitAnimation';

type ConfirmModalProps = {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
};

export default function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  danger = false,
  onConfirm,
  onCancel,
  children,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  const drag = useDraggable();
  const exiting = useExitAnimation(open);
  useEscapeClose(open, onCancel);
  if (!open && !exiting) return null;
  const confirm = confirmLabel ?? t('confirm');
  const cancel = cancelLabel ?? t('cancel');

  return (
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center bg-bark/40 backdrop-blur-sm overflow-y-auto ${open ? 'animate-backdropIn' : 'animate-backdropOut'}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        aria-describedby="confirm-modal-message"
        className={`bg-linen border border-wheat rounded-rustic shadow-xl p-6 w-full max-w-md mx-4 ${open ? 'animate-modalIn' : 'animate-modalOut'}`}
        style={drag.pos ? { transform: `translate(${drag.pos.x}px, ${drag.pos.y}px)` } : undefined}
      >
        <h3
          id="confirm-modal-title"
          className="text-lg font-semibold text-bark mb-2 cursor-move select-none"
          onMouseDown={drag.onMouseDown}
        >
          {danger ? '⚠️ ' : 'ℹ️ '}
          {title}
        </h3>
        <p id="confirm-modal-message" className="text-sm text-wood mb-6 whitespace-pre-line">
          {message}
        </p>
        {children}
        <div className="flex gap-3 justify-end pt-4 border-t border-wheat/30">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
          >
            {cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`py-2 px-4 rounded-rustic text-sm font-semibold transition text-linen ${
              danger
                ? 'bg-rust hover:bg-rust-dark'
                : 'bg-moss hover:bg-moss-dark'
            }`}
          >
            {confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

